import { CoreApiVersionPath, RosaCoreApiService } from '@/rosa';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    type DashboardId,
    DashboardLayoutType,
    type DashboardMode,
    type DashboardScopeType,
    type TailoredDashboard,
    type TailoredDashboardId,
    type TailoredDashboardLayout
} from '../../interfaces/Dashboard.interface';
import type { ProfileId } from '../../interfaces/Profile.interface';
import type { TailoredWidgetId } from '../../interfaces/TailoredWidget.interface';

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreTailoredDashboardsProvider {
    private _rosaCoreApiService = inject(RosaCoreApiService);

    createDashboard$(name: string, type: DashboardScopeType): Observable<DashboardId> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V3}dashboards`, { name, type, code: null });
    }

    editDashboard$(dashboardId: TailoredDashboardId, name: string): Observable<void> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V3}dashboards/${dashboardId}`, { name });
    }

    editDashboardType$(dashboardId: TailoredDashboardId, dashboardType: DashboardScopeType): Observable<void> {
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V3}dashboards/${dashboardId}/type`, { type: dashboardType });
    }

    deleteDashboard$(dashboardId: TailoredDashboardId): Observable<void> {
        return this._rosaCoreApiService.delete(`${CoreApiVersionPath.V3}dashboards/${dashboardId}`);
    }

    fetchDashboards$(): Observable<TailoredDashboard[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V3}dashboards/me`);
    }

    fetchDashboardLayout$(dashboardId: DashboardId, mode: DashboardMode): Observable<TailoredDashboardLayout> {
        type RouteResponse = {
            widgetId: TailoredWidgetId;
            positionX: number;
            positionY: number;
            width: number;
            height: number;
        }[];

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V3}dashboards/${dashboardId}/layout?mode=${mode}`).pipe(
            map(widgets => {
                return {
                    layoutType: DashboardLayoutType.TAILORED as DashboardLayoutType.TAILORED,
                    widgets: widgets.map(widget => ({
                        widgetId: widget.widgetId,
                        position: {
                            x: widget.positionX,
                            y: widget.positionY,
                            width: widget.width,
                            height: widget.height
                        }
                    }))
                };
            })
        );
    }

    saveDashboardModeLayouts$(
        dashboardId: DashboardId,
        modeLayouts: { mode: DashboardMode; layout: TailoredDashboardLayout }[]
    ): Observable<void> {
        interface RouteDto {
            dashboardLayouts: {
                mode: DashboardMode;
                widgetPositions: {
                    widgetId: TailoredWidgetId;
                    positionX: number;
                    positionY: number;
                    width: number;
                    height: number;
                }[];
            }[];
        }

        const body: RouteDto = {
            dashboardLayouts: modeLayouts.map(modeLayout => ({
                mode: modeLayout.mode,
                widgetPositions: modeLayout.layout.widgets.map(widget => ({
                    widgetId: widget.widgetId,
                    positionX: widget.position.x,
                    positionY: widget.position.y,
                    width: widget.position.width,
                    height: widget.position.height
                }))
            }))
        };
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V3}dashboards/${dashboardId}/layouts`, body);
    }

    fetchDashboardProfilesAccesses$(dashboardId: DashboardId): Observable<ProfileId[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V4}dashboards/${dashboardId}/profiles`);
    }
}
